import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { listMemories, type Memory } from "../../api/threads/memoriesApi";
import { useLoad } from "../../shared/loading/useLoad";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { deferredToItsThread } from "./memoryActions";
import MemoryRow from "./MemoryRow";
import { onMemoriesChanged } from "./memoryEvents";

interface ThreadMemoriesProps {
  readonly projectId: string;
  readonly threadId: string;
  /** Changes when the Thread's log shows a new memory save, so the list reads again. */
  readonly revision: number;
  /** The memory whose review step waits in this Thread (`memoryUnderReview`): its card only shows it. */
  readonly reviewedHere: string | null;
}

interface ThreadMemoryLists {
  /** The memory this Thread keeps, if it is a run of "Keep a memory". */
  readonly keptHere: readonly Memory[];
  readonly keptFromHere: readonly Memory[];
}

/**
 * A Thread's memories (ADR-0031 D5), where they are edited: the memory a "Keep a memory"
 * Thread keeps, also once its Workflow has completed, and the memories kept from this
 * Thread, each linked to its own small Thread. It reads again when a memory changes in
 * either pane or the log shows a new save. Nothing shows while there are none.
 */
export default function ThreadMemories({ projectId, threadId, revision, reviewedHere }: ThreadMemoriesProps) {
  const { t } = useTranslation();
  const load = useCallback(
    async (signal: AbortSignal): Promise<ThreadMemoryLists & { readonly revision: number }> => {
      const [keptHere, keptFromHere] = await Promise.all([
        listMemories(projectId, null, signal, { keptIn: threadId }),
        listMemories(projectId, null, signal, { attachedTo: threadId }),
      ]);
      // Only this Thread's own memory, whatever the catalogue returns for the filter.
      return { keptHere: keptHere.filter((memory) => memory.keptIn === threadId), keptFromHere, revision };
    },
    [projectId, threadId, revision],
  );
  const { state, reload, refresh } = useLoad(load);
  useEffect(() => onMemoriesChanged(refresh), [refresh]);
  if (state.status === "failed") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />;
  if (state.status !== "loaded") return null;
  const { keptHere, keptFromHere } = state.value;
  return (
    <>
      {keptHere.length === 0 ? null : (
        <section className="app-card space-y-2 p-3" aria-label={t("memories.inThread.keptHere")}>
          <h3 className="app-title text-sm font-semibold">{t("memories.inThread.keptHere")}</h3>
          <ul className="space-y-3">
            {keptHere.map((memory) => <MemoryRow key={memory.memoryId} projectId={projectId} memory={memory} onChanged={refresh} underReview={memory.memoryId === reviewedHere} />)}
          </ul>
        </section>
      )}
      {keptFromHere.length === 0 ? null : (
        <details className="app-card p-3">
          <summary className="app-title cursor-pointer text-sm font-semibold">{t("memories.inThread.title", { count: keptFromHere.length })}</summary>
          <ul className="mt-2 space-y-3">
            {keptFromHere.map((memory) => <MemoryRow key={memory.memoryId} projectId={projectId} memory={memory} onChanged={refresh} underReview={deferredToItsThread(memory)} />)}
          </ul>
        </details>
      )}
    </>
  );
}
